import { Feature, FeatureCollection, LineString } from "geojson";
import { GeoJSONSource } from "maplibre-gl";
import { useEffect, useState } from "react";
import { Easing, Img, interpolate, staticFile, useCurrentFrame, useDelayRender, useVideoConfig } from "remotion";
import { length, lineSliceAlong, along, simplify } from "@turf/turf"
import { useMap } from "./MapContext";

type Props = {
    id: string;
    geoJSONFile: string;
    position: number;
    animated: boolean;
    animatedDistance: number;
}

const ROUTE_WIDTH = 7;
const BORDER_WIDTH = 9;
const BORDER_COLOR = "#f97316";
const ROUTE_REMAINING_COLOR = "#fff";
const ROUTE_COLOR = "#f2c400";
const INITIAL_ZOOM = 5;
const ZOOM = 7;
const ZOOM_DURATION = 1; // s

async function loadRoute(filename: string): Promise<FeatureCollection<LineString>> {
    let url = staticFile(filename)
    let response = await fetch(url);
    return response.json();
}

export const Route: React.FC<Props> = ({ geoJSONFile, position, animated, id, animatedDistance }) => {
    const { map, loaded } = useMap();
    const frame = useCurrentFrame();
    const {durationInFrames, fps} = useVideoConfig();
    const { delayRender, continueRender } = useDelayRender();
    const [handle] = useState(() => delayRender('Loading route...'));
    const [isReady, setIsReady] = useState(false);
    const [route, setRoute] = useState<Feature<LineString> | null>(null);
    const [markerPosition, setMarkerPosition] = useState<[number, number] | null>(null);
    const [focusPoint, setFocusPoint] = useState<[number, number] | null>(null);

    const progress = interpolate(frame, [0, durationInFrames - 1], [position - animatedDistance, position + animatedDistance], {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
        easing: Easing.linear
    });

    const zoom = interpolate(frame, [0, ZOOM_DURATION * fps], [INITIAL_ZOOM, ZOOM], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
        easing: Easing.linear,
    });

    useEffect(() => {
        if (!loaded) return;

        let cancelled = false;

        const setup = async () => {
            const geojson = await loadRoute(geoJSONFile);
            if (cancelled) return;

            let simplified = simplify(geojson, {
                tolerance: 0.005,
                highQuality: true
            });

            setRoute(simplified.features[0]);

            // data source for preview
            map.addSource('preview-' + id, {
                type: 'geojson',
                data: simplified.features[0], // only render the first feature in the file
            });

            // data source for animated route
            map.addSource('route-' + id, {
                type: 'geojson',
                data: simplified.features[0],
            });

            // border (wider than actual route to be visible)
            map.addLayer({
                id: 'route-border-' + id,
                type: 'line',
                source: 'preview-' + id,
                layout: {
                    'line-cap': 'round',
                    'line-join': 'round'
                },
                paint: {
                    'line-color': BORDER_COLOR,
                    'line-width': BORDER_WIDTH
                }
            });

            // actual route
            map.addLayer({
                id: 'route-inner-' + id,
                type: 'line',
                source: 'preview-' + id,
                layout: {
                    'line-cap': 'round',
                    'line-join': 'round'
                },
                paint: {
                    'line-color': ROUTE_REMAINING_COLOR,
                    'line-width': ROUTE_WIDTH
                }
            });

            // animated route
            map.addLayer({
                id: 'route-animated-' + id,
                type: 'line',
                source: 'route-' + id,
                layout: {
                    'line-cap': 'round',
                    'line-join': 'round'
                },
                paint: {
                    'line-color': ROUTE_COLOR,
                    'line-width': ROUTE_WIDTH
                }
            });

            // move camera to focus point position and initial zoom before the
            // first frame is captured, so we never render the default view
            const routeDistance = length(simplified.features[0]);
            const focusPointDistance = routeDistance * position;
            const point = along(simplified.features[0], focusPointDistance);
            setFocusPoint([point.geometry.coordinates[0], point.geometry.coordinates[1]]);
            map.jumpTo({
                center: [point.geometry.coordinates[0], point.geometry.coordinates[1]],
                zoom: animated ? INITIAL_ZOOM : map.getZoom(),
            });

            // wait for the new tiles to be rendered before releasing the frame
            await new Promise<void>((resolve) => {
                if (cancelled) {
                    resolve();
                    return;
                }
                map.once('idle', () => resolve());
            });

            if (cancelled) return;
            setIsReady(true);
        };

        setup();

        return () => {
            cancelled = true;

            const layerIds = [
                `route-border-${id}`,
                `route-inner-${id}`,
                `route-animated-${id}`,
            ];

            const sourceIds = [
                `preview-${id}`,
                `route-${id}`,
            ];

            layerIds.forEach(layerId => {
                if (map.getLayer(layerId)) {
                    map.removeLayer(layerId);
                }
            });

            sourceIds.forEach(sourceId => {
                if (map.getSource(sourceId)) {
                    map.removeSource(sourceId);
                }
            });
        };

    }, [map, loaded, geoJSONFile, position, animated, id]);

    useEffect(() => {
        if (isReady) {
            continueRender(handle);
        }
    }, [isReady, handle, continueRender]);

    useEffect(() => {
        if(!route || !animated) return;

        const routeDistance = length(route);
        const currentDistance = Math.max(0.001, routeDistance * progress);
        const slicedLine = lineSliceAlong(route, 0, currentDistance);

        const source = map?.getSource('route-' + id) as GeoJSONSource | undefined;
        source?.setData(slicedLine);

        // calulate position of marker
        const point = along(route, currentDistance);
        const coordinates = point.geometry.coordinates;
        setMarkerPosition([coordinates[0], coordinates[1]]);

        // animate zoom
        map.jumpTo({
            center: focusPoint || undefined,
            zoom: zoom
        });
    }, [frame, route, zoom, focusPoint]);

    if(!markerPosition) return <></>;

    const point = map.project(markerPosition);

    return (
        <Img
            src={staticFile("marker.png")}
            style={{
                position: "absolute",
                left: point.x,
                top: point.y,
                width: 78,
                height: 108,
                transform: "translate(-50%, -100%)",
                pointerEvents: "none",
            }}
        />
    );
};